import { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Form, Input } from "semantic-ui-react";

import { PriceInput } from "components/PriceInput";

import type { ControllerRenderProps, FieldValues, Path, PathValue } from "react-hook-form";
import type { FormFieldProps, InputOnChangeData, InputProps } from "semantic-ui-react";

type TypeFormInput<K extends FieldValues, T extends Path<K>> = {
  name: T;
  inputProps?: Omit<InputProps, "onChange">;
  formFieldProps?: FormFieldProps;
  onChange?: (value: PathValue<K, T>) => void;
  isPriceInput?: boolean;
  nullable?: boolean;
  preventAutofill?: boolean;
};

const isPathValue = <K extends FieldValues, T extends Path<K>>(value: unknown, target: PathValue<K, T>): value is PathValue<K, T> =>
  typeof target === "object" || typeof value === typeof target;

// TODO remove the export once usage outside this component is replaced by useFormInput
export const parsePriceFromInput = (input: string = ""): number => {
  let price = input;

  if (input.match(/^(\d{1,3}\.)*\d{3},\d{1,3}$/)) price = input.replace(".", "");
  if (price.match(/,\d{1,3}$/)) price = price.replace(",", ".");
  return Number(price.replace(",", ""));
};

export const FormInputHelper = <K extends FieldValues>() => {
  const FormInput = <T extends Path<K>>({ name, inputProps, formFieldProps, onChange, isPriceInput, nullable, preventAutofill }: TypeFormInput<K, T>) => {
    const methods = useFormContext<K>();
    const [hasFocused, setHasFocused] = useState(false);

    const isAutofillGuarded = preventAutofill && !hasFocused;
    const isPasswordField = inputProps?.type === "password";
    const type = isAutofillGuarded && isPasswordField ? "text" : inputProps?.type;
    const autoComplete = preventAutofill ? (isPasswordField ? "new-password" : "off") : inputProps?.autoComplete;

    const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
      setHasFocused(true);
      inputProps?.onFocus?.(event);
    };
    const handleOnChange = ({ value }: InputOnChangeData, field: ControllerRenderProps<K, T>) => {
      if (nullable && value === "") {
        field.onChange(null);
      } else if (isPriceInput || inputProps?.type === "number") {
        const numericValue = parsePriceFromInput(value);
        field.onChange(numericValue);
        if (onChange && isPathValue(numericValue, field.value)) onChange(numericValue);
      } else {
        field.onChange(value);
        if (onChange && isPathValue(value, field.value)) onChange(value);
      }
    };

    return (
      <Controller
        name={name}
        control={methods.control}
        render={({ field, fieldState: { invalid, isTouched } }) => (
          <Form.Field {...formFieldProps} error={invalid && isTouched}>
            {!isPriceInput && (
              <Input
                {...field}
                value={field.value ?? ""}
                {...inputProps}
                type={type}
                autoComplete={autoComplete}
                readOnly={isAutofillGuarded || inputProps?.readOnly}
                onFocus={handleFocus}
                onChange={(_, data) => handleOnChange(data, field)}
              />
            )}

            {isPriceInput && (
              <PriceInput {...field} {...inputProps} onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleOnChange({ value: event.target.value }, field)} />
            )}
          </Form.Field>
        )}
      />
    );
  };

  return FormInput;
};
